import { supabase, isSupabaseConfigured } from './client';

export const storage = {
  /**
   * Available storage buckets
   */
  buckets: ['project-images', 'gallery', 'testimonial-images', 'website-assets'],

  /**
   * Upload an asset file to a Supabase Storage bucket
   * @param {string} bucketName - 'project-images' | 'gallery' | 'testimonial-images' | 'website-assets'
   * @param {File} file - File object from file input
   * @param {string} [customPath] - Optional custom path or subfolder
   */
  async uploadFile(bucketName = 'website-assets', file, customPath = '') {
    if (!isSupabaseConfigured) {
      throw new Error('Supabase Storage is not configured.');
    }

    if (!file) {
      throw new Error('No file provided for upload.');
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Allowed formats: JPG, PNG, WEBP, SVG, GIF, PDF.');
    }

    // Validate size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size exceeds 10MB limit.');
    }

    const cleanFileName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
    const timestamp = Date.now();
    const storagePath = customPath
      ? `${customPath}/${timestamp}-${cleanFileName}`
      : `${timestamp}-${cleanFileName}`;

    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (error) {
      throw error;
    }

    const { data: publicUrlData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(data.path);

    return {
      name: file.name,
      storagePath: data.path,
      publicUrl: publicUrlData.publicUrl,
      size: file.size,
      type: file.type,
      bucket: bucketName,
    };
  },

  /**
   * List files within a Supabase Storage bucket
   * @param {string} bucketName
   * @param {string} [folderPath]
   */
  async listFiles(bucketName = 'website-assets', folderPath = '') {
    if (!isSupabaseConfigured) return [];

    try {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .list(folderPath, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' },
        });

      if (error) {
        console.error(`Error listing files in ${bucketName}:`, error);
        return [];
      }

      // Filter out folder placeholders and generate public URLs
      return (data || [])
        .filter((item) => item.name && item.name !== '.emptyFolderPlaceholder')
        .map((item) => {
          const filePath = folderPath ? `${folderPath}/${item.name}` : item.name;
          const { data: urlData } = supabase.storage.from(bucketName).getPublicUrl(filePath);
          return {
            ...item,
            bucket: bucketName,
            storagePath: filePath,
            publicUrl: urlData.publicUrl,
          };
        });
    } catch (err) {
      console.error(`Exception listing storage files in ${bucketName}:`, err);
      return [];
    }
  },

  /**
   * Delete an object from Supabase Storage
   */
  async deleteFile(bucketName, storagePath) {
    if (!isSupabaseConfigured || !storagePath) return false;

    const { error } = await supabase.storage
      .from(bucketName)
      .remove([storagePath]);

    if (error) {
      console.error(`Error deleting storage file ${storagePath} from ${bucketName}:`, error);
      throw error;
    }

    return true;
  },

  /**
   * Get public URL for a storage path
   */
  getPublicUrl(bucketName, storagePath) {
    if (!storagePath) return '';
    if (storagePath.startsWith('http')) return storagePath;
    const { data } = supabase.storage.from(bucketName).getPublicUrl(storagePath);
    return data?.publicUrl || '';
  },
};

export default storage;
